import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Skeleton,
  Alert,
  IconButton,
  Chip
} from '@mui/material';
import {
  ArrowBack,
  ChevronLeft,
  ChevronRight,
  WbSunny,
  NightsStay,
  AccessTime
} from '@mui/icons-material';
import { format, parseISO, addDays, isSameDay, getHours, startOfDay } from 'date-fns';

const MobileDateTimePicker = forwardRef(({
  availableTimes = [],
  selectedTime,
  onTimeSelect,
  onBack,
  loading = false,
  timezone
}, ref) => {
  const dateStripRef = useRef(null);
  const timeSlotsRef = useRef(null);

  // State
  const [selectedDate, setSelectedDate] = useState(null);
  const [dateRange, setDateRange] = useState([]);
  const [timesByDate, setTimesByDate] = useState({});
  const [availableDates, setAvailableDates] = useState(new Set());

  // Expose methods to parent
  useImperativeHandle(ref, () => ({
    scrollToTimes: () => {
      if (timeSlotsRef.current) {
        timeSlotsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }));

  // Generate date range (today + 30 days)
  useEffect(() => {
    const dates = [];
    const today = startOfDay(new Date());
    for (let i = 0; i < 30; i++) {
      dates.push(addDays(today, i));
    }
    setDateRange(dates);
  }, []);

  // Process available times into grouped structure
  useEffect(() => {
    const grouped = {};
    const available = new Set();

    availableTimes.forEach(time => {
      if (!time?.date) return;

      const dateKey = time.date;
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(time);
      available.add(dateKey);
    });

    // Sort times within each date
    Object.keys(grouped).forEach(date => {
      grouped[date].sort((a, b) => new Date(a.iso) - new Date(b.iso));
    });

    setTimesByDate(grouped);
    setAvailableDates(available);

    // Auto-select first available date
    if (available.size > 0 && !selectedDate) {
      const firstDate = Array.from(available).sort()[0];
      setSelectedDate(firstDate);
    }
  }, [availableTimes]);

  // Handle date selection
  const handleDateSelect = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    if (availableDates.has(dateStr)) {
      setSelectedDate(dateStr);

      // Auto-scroll to time slots after a brief delay
      setTimeout(() => {
        if (timeSlotsRef.current) {
          timeSlotsRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
    }
  };

  // Scroll date strip
  const scrollDates = (direction) => {
    if (dateStripRef.current) {
      const scrollAmount = direction === 'left' ? -200 : 200;
      dateStripRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  // Get times for selected date, grouped by morning/afternoon
  const getTimesForDate = () => {
    if (!selectedDate || !timesByDate[selectedDate]) {
      return { morning: [], afternoon: [] };
    }

    const times = timesByDate[selectedDate];
    const morning = [];
    const afternoon = [];

    times.forEach(time => {
      const hour = getHours(parseISO(time.iso));
      if (hour < 12) {
        morning.push(time);
      } else {
        afternoon.push(time);
      }
    });

    return { morning, afternoon };
  };

  // Handle time selection
  const handleTimeSelect = (time) => {
    const timeString = format(parseISO(time.iso), 'h:mm a');
    const enhancedTime = {
      ...time,
      timeString,
      formatted: `${format(parseISO(selectedDate), 'EEEE, MMMM d, yyyy')} at ${timeString}`,
      time_display: `${format(parseISO(selectedDate), 'EEEE, MMMM d, yyyy')} at ${timeString}`
    };
    onTimeSelect(enhancedTime);
  };

  // Loading state
  if (loading) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={onBack} sx={{ mr: 1 }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" fontWeight="600">
            Select Date & Time
          </Typography>
        </Box>

        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1 }}>
            {[1, 2, 3, 4, 5].map(i => (
              <Skeleton key={i} variant="rounded" width={64} height={72} />
            ))}
          </Box>
        </Paper>

        <Paper sx={{ p: 2 }}>
          <Skeleton variant="text" width={120} height={28} sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {[1, 2, 3, 4, 5, 6].map(i => (
              <Skeleton key={i} variant="rounded" width={100} height={48} />
            ))}
          </Box>
        </Paper>
      </Box>
    );
  }

  // No times available
  if (!loading && availableTimes.length === 0) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={onBack} sx={{ mr: 1 }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" fontWeight="600">
            Select Date & Time
          </Typography>
        </Box>

        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <AccessTime sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No Available Times
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Sorry, there are no available appointments in the next week.
            Please try a different practitioner or contact the clinic directly.
          </Typography>
          <Button variant="contained" onClick={onBack}>
            Choose Another Practitioner
          </Button>
        </Paper>
      </Box>
    );
  }

  const { morning, afternoon } = getTimesForDate();

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={onBack} sx={{ mr: 1 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h6" fontWeight="600">
          Select Date & Time
        </Typography>
      </Box>

      {/* Horizontal Date Strip */}
      <Paper sx={{ mb: 3, overflow: 'hidden' }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          bgcolor: 'primary.main',
          color: 'white',
          py: 1,
          px: 2
        }}>
          <Typography variant="subtitle2" fontWeight="600">
            Choose a Date
          </Typography>
        </Box>

        <Box sx={{ position: 'relative' }}>
          {/* Left scroll button */}
          <IconButton
            onClick={() => scrollDates('left')}
            sx={{
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              bgcolor: 'white',
              boxShadow: 2,
              '&:hover': { bgcolor: 'grey.100' }
            }}
            size="small"
          >
            <ChevronLeft />
          </IconButton>

          {/* Date strip */}
          <Box
            ref={dateStripRef}
            sx={{
              display: 'flex',
              overflowX: 'auto',
              scrollBehavior: 'smooth',
              py: 2,
              px: 5,
              gap: 1,
              '&::-webkit-scrollbar': { display: 'none' },
              scrollbarWidth: 'none'
            }}
          >
            {dateRange.map((date, index) => {
              const dateStr = format(date, 'yyyy-MM-dd');
              const isAvailable = availableDates.has(dateStr);
              const isSelected = selectedDate === dateStr;
              const isToday = isSameDay(date, new Date());

              return (
                <Box
                  key={index}
                  onClick={() => handleDateSelect(date)}
                  sx={{
                    minWidth: 64,
                    textAlign: 'center',
                    py: 1.5,
                    px: 1,
                    borderRadius: 2,
                    cursor: isAvailable ? 'pointer' : 'default',
                    bgcolor: isSelected
                      ? 'primary.main'
                      : isAvailable
                        ? 'grey.100'
                        : 'transparent',
                    color: isSelected
                      ? 'white'
                      : isAvailable
                        ? 'text.primary'
                        : 'text.disabled',
                    opacity: isAvailable ? 1 : 0.5,
                    transition: 'all 0.2s ease',
                    '&:hover': isAvailable && !isSelected ? {
                      bgcolor: 'primary.light',
                      color: 'white',
                      transform: 'scale(1.05)'
                    } : {},
                    border: isToday && !isSelected ? '2px solid' : 'none',
                    borderColor: 'primary.main'
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      fontWeight: isSelected ? 700 : 500,
                      textTransform: 'uppercase',
                      fontSize: '0.65rem'
                    }}
                  >
                    {isToday ? 'Today' : format(date, 'EEE')}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      lineHeight: 1.2
                    }}
                  >
                    {format(date, 'd')}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      fontSize: '0.65rem'
                    }}
                  >
                    {format(date, 'MMM')}
                  </Typography>
                  {/* Availability indicator */}
                  {isAvailable && (
                    <Box sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: isSelected ? 'white' : 'primary.main',
                      mx: 'auto',
                      mt: 0.5
                    }} />
                  )}
                </Box>
              );
            })}
          </Box>

          {/* Right scroll button */}
          <IconButton
            onClick={() => scrollDates('right')}
            sx={{
              position: 'absolute',
              right: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              bgcolor: 'white',
              boxShadow: 2,
              '&:hover': { bgcolor: 'grey.100' }
            }}
            size="small"
          >
            <ChevronRight />
          </IconButton>
        </Box>

        {/* Legend */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 2,
          py: 1,
          borderTop: '1px solid',
          borderColor: 'divider'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{
              width: 6, height: 6,
              borderRadius: '50%',
              bgcolor: 'primary.main'
            }} />
            <Typography variant="caption" color="text.secondary">
              Available
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Time Slots */}
      <Paper ref={timeSlotsRef} sx={{ overflow: 'hidden' }}>
        {selectedDate ? (
          <>
            {/* Selected date header */}
            <Box sx={{
              bgcolor: 'grey.100',
              py: 1.5,
              px: 2,
              borderBottom: '1px solid',
              borderColor: 'divider'
            }}>
              <Typography variant="subtitle1" fontWeight="600">
                {format(parseISO(selectedDate), 'EEEE, MMMM d, yyyy')}
              </Typography>
            </Box>

            <Box sx={{ p: 2 }}>
              {/* Morning slots */}
              {morning.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <WbSunny sx={{ fontSize: 18, color: 'warning.main', mr: 1 }} />
                    <Typography variant="subtitle2" fontWeight="600" color="warning.dark">
                      Morning
                    </Typography>
                    <Chip
                      label={`${morning.length} slots`}
                      size="small"
                      sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                    />
                  </Box>

                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 1
                  }}>
                    {morning.map((time, index) => {
                      const timeStr = format(parseISO(time.iso), 'h:mm a');
                      const isSelected = selectedTime?.iso === time.iso;

                      return (
                        <Button
                          key={index}
                          variant={isSelected ? 'contained' : 'outlined'}
                          onClick={() => handleTimeSelect(time)}
                          sx={{
                            height: 52,
                            fontSize: '0.9rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: 2,
                            transition: 'all 0.2s ease',
                            '&:active': {
                              transform: 'scale(0.96)'
                            }
                          }}
                        >
                          {timeStr}
                        </Button>
                      );
                    })}
                  </Box>
                </Box>
              )}

              {/* Afternoon slots */}
              {afternoon.length > 0 && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <NightsStay sx={{ fontSize: 18, color: 'primary.main', mr: 1 }} />
                    <Typography variant="subtitle2" fontWeight="600" color="primary.main">
                      Afternoon
                    </Typography>
                    <Chip
                      label={`${afternoon.length} slots`}
                      size="small"
                      sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                    />
                  </Box>

                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 1
                  }}>
                    {afternoon.map((time, index) => {
                      const timeStr = format(parseISO(time.iso), 'h:mm a');
                      const isSelected = selectedTime?.iso === time.iso;

                      return (
                        <Button
                          key={index}
                          variant={isSelected ? 'contained' : 'outlined'}
                          color="primary"
                          onClick={() => handleTimeSelect(time)}
                          sx={{
                            height: 52,
                            fontSize: '0.9rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: 2,
                            transition: 'all 0.2s ease',
                            '&:active': {
                              transform: 'scale(0.96)'
                            }
                          }}
                        >
                          {timeStr}
                        </Button>
                      );
                    })}
                  </Box>
                </Box>
              )}

              {/* No times for selected date */}
              {morning.length === 0 && afternoon.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary">
                    No available times for this date.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Please select another date above.
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        ) : (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <AccessTime sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">
              Select a date above to see available times
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Timezone info */}
      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          Times shown in {timezone?.replace('_', ' ').replace('/', ' / ')}
        </Typography>
      </Box>
    </Box>
  );
});

MobileDateTimePicker.displayName = 'MobileDateTimePicker';

export default MobileDateTimePicker;
